import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
  signal,
  ViewChild,
} from "@angular/core";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { HttpErrorResponse } from "@angular/common/http";
import { DatePipe } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TablerIconsModule } from "angular-tabler-icons";
import { catchError, finalize, EMPTY, of, switchMap, tap } from "rxjs";
import { MaterialModule } from "src/app/material.module";
import { StoreService } from "src/app/services/apps/store/store.service";
import { BillingService } from "src/app/pages/billing/billing.service";
import { OnboardingModalService } from "src/app/pages/onboarding/onboarding-modal.service";
import { ProfileService } from "src/app/pages/profile/profile.service";
import { ToastService } from "src/app/shared/services/toast.service";
import { LoaderService } from "src/app/shared/components/loader/loader.service";
import {
  TableColumnTemplateDirective,
  TreesloGridComponent,
  type TreesloGridModel,
} from "src/app/shared/components/treeslo-grid";
import { AcceptInvitationResponse, PendingStoreInvitation, StoreInvitationErrorType } from "../store";
import { StoreCardComponent } from "../store-card/store-card.component";

@Component({
  selector: "app-shared-with-me",
  standalone: true,
  imports: [
    MaterialModule,
    TranslateModule,
    TablerIconsModule,
    DatePipe,
    TreesloGridComponent,
    TableColumnTemplateDirective,
    StoreCardComponent,
    DragDropModule,
  ],
  templateUrl: "./shared-with-me.component.html",
  styleUrl: "./shared-with-me.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedWithMeComponent implements OnInit {
  private readonly storeService = inject(StoreService);
  private readonly billingService = inject(BillingService);
  private readonly profileService = inject(ProfileService);
  private readonly onboardingModalService = inject(OnboardingModalService);
  private readonly route = inject(ActivatedRoute);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly loader = inject(LoaderService);
  /** Cards or grid/table view mode inherited from the parent. */
  readonly viewMode = input<"cards" | "grid">("cards");

  /** When true, pending invitation cards cannot be dragged (e.g. grouped mode with no shared stores). */
  readonly dragDisabled = input<boolean>(false);

  /** Pending invitations from resolver (to avoid duplicate API calls). */
  readonly initialInvitations = input<PendingStoreInvitation[]>([]);

  /** Emitted after an invitation is accepted so the parent can refresh stores. */
  readonly accepted = output<string>();

  /** Emitted after the invitations list is loaded with the current count. */
  readonly invitationsCount = output<number>();

  /** Emitted when a card drag begins (so the parent can lock the section toggle). */
  readonly dragStarted = output<void>();

  /** Emitted when a card drag ends (drop or cancel). */
  readonly dragEnded = output<void>();

  @ViewChild("pendingGrid") pendingGrid?: TreesloGridComponent<PendingStoreInvitation>;

  readonly invitations = signal<PendingStoreInvitation[]>([]);
  readonly acceptingStoreId = signal<string | null>(null);
  readonly emailNotVerified = signal(false);

  pendingGridConfig: TreesloGridModel<PendingStoreInvitation> | null = null;

  ngOnInit(): void {
    this.pendingGridConfig = this.buildPendingGridConfig();
    const initial = this.initialInvitations();
    if (initial.length > 0) {
      // Use invitations from resolver to avoid duplicate API call
      this.invitations.set(initial);
      this.invitationsCount.emit(initial.length);
    } else {
      // Load from API if not provided
      this.load();
    }
  }

  accept(invitation: PendingStoreInvitation): void {
    if (this.acceptingStoreId()) return;
    this.acceptingStoreId.set(invitation.storeId);
    this.loader.show();

    this.storeService
      .acceptInvitation(invitation.storeId)
      .pipe(
        // Remove from pending list in the same synchronous tick as stores.set(),
        // so both signals update before Angular runs change detection — no flash.
        tap(() => {
          this.invitations.update((list) =>
            list.filter((i) => i.storeId !== invitation.storeId),
          );
          this.invitationsCount.emit(this.invitations().length);
        }),
        switchMap((res) =>
          this.billingService.refreshCustomerBootstrapCache().pipe(
            catchError(() => of(null)),
            switchMap(() => [res]),
          ),
        ),
        finalize(() => {
          this.loader.hide();
          this.acceptingStoreId.set(null);
        }),
      )
      .subscribe({
        next: (res: AcceptInvitationResponse) => {
          if (res.alreadyLinked) {
            this.toast.info(
              this.translate.instant("stores.invitations.alreadyLinked"),
            );
          } else {
            this.toast.success(
              this.translate.instant("stores.invitations.accepted", {
                name: invitation.name,
              }),
            );
          }
          // Onboarding finishes here only when every store is now shared with the
          // user; with an owned store it still has its own steps. Emit afterwards so
          // the parent refreshes with the final onboarding state.
          this.onboardingModalService
            .completeOnboardingWhenDelegatedReady(this.route.snapshot)
            .subscribe(() => this.accepted.emit(invitation.storeId));
        },
        error: (err: HttpErrorResponse) => this.handleAcceptError(err, invitation.storeId),
      });
  }

  private handleAcceptError(err: HttpErrorResponse, storeId: string): void {
    const errorType = err.error?.errorType as StoreInvitationErrorType | undefined;
    if (errorType === "invitationNotFound" || errorType === "invitationNoLongerValid") {
      this.invitations.update((list) => list.filter((i) => i.storeId !== storeId));
      this.invitationsCount.emit(this.invitations().length);
    }
    const key = errorType ? `stores.invitations.errors.${errorType}` : null;
    const translated = key ? this.translate.instant(key) : null;
    const fallback = err.error?.message ?? this.translate.instant("toast_errors.acceptInvitationFailed");
    this.toast.error(translated && translated !== key ? translated : fallback);
  }

  isAmazonProvider(provider: string): boolean {
    return provider?.toLowerCase().includes("amazon") ?? false;
  }

  getProviderLogo(provider: string): string {
    return `assets/images/providers-logos/${provider?.toLowerCase()}.png`;
  }

  private load(): void {
    if (this.profileService.userProfile()?.emailVerified === false) {
      this.emailNotVerified.set(true);
      return;
    }
    this.loader.show();
    this.storeService
      .getPendingInvitations()
      .pipe(
        finalize(() => this.loader.hide()),
        catchError(() => EMPTY),
      )
      .subscribe((invitations) => {
        this.invitations.set(invitations);
        this.invitationsCount.emit(invitations.length);
        this.pendingGrid?.refresh();
      });
  }

  private buildPendingGridConfig(): TreesloGridModel<PendingStoreInvitation> {
    return {
      translationPrefix: "stores",
      striped: true,
      columns: [
        { displayfieldName: "grid_pending_name", fieldName: "name" },
        { displayfieldName: "grid_pending_provider", fieldName: "provider" },
        { displayfieldName: "grid_pending_invitedAt", fieldName: "invitedAt" },
        { displayfieldName: "grid_pending_expiresAt", fieldName: "expiresAt" },
      ],
      actionsGridRows: [
        {
          icon: "check",
          tooltip: "sharedWithMe.acceptAction",
          disabled: () => !!this.acceptingStoreId(),
          action: (row) => this.accept(row),
        },
      ],
      load: (_req) => of({ items: this.invitations(), cursor: null }),
      paginator: { pageSizeOptions: [10, 25, 50], pageSize: 10, cursors: [null] },
    };
  }
}
